import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { timeFormat } from "d3-time-format";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachVerticalLine from "./wrapper/EachVerticalLine";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class VerticalLine extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawVerticalLine = this.handleDrawVerticalLine.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDragVerticalLine = this.handleDragVerticalLine.bind(this);
    this.handleDragVerticalLineComplete =
      this.handleDragVerticalLineComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState =
      isHoverForInteractiveType("verticalLines").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragVerticalLine(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragVerticalLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { verticalLines } = this.props;
      const newVerticalLines = verticalLines.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              xValue: override.xValue,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newVerticalLines, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { enabled } = this.props;
    if (!enabled) return;

    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.xValue)) {
      this.setState(
        {
          current: {
            xValue: xyValue[0],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawVerticalLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.xValue)) {
      this.setState({
        current: {
          xValue: xyValue[0],
        },
      });
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { enabled, verticalLines, appearance, chartSectionId } = this.props;
    const { current } = this.state;

    if (!enabled) return;

    const morePropsSectionId = moreProps?.chartConfig?.id;
    if (isDefined(chartSectionId) && isDefined(morePropsSectionId)) {
      if (String(chartSectionId) !== String(morePropsSectionId)) {
        return;
      }
    }

    const currentCharts = moreProps?.currentCharts;
    if (Array.isArray(currentCharts) && isDefined(chartSectionId)) {
      const matchesChart = currentCharts.some(
        (id) => String(id) === String(chartSectionId),
      );
      if (!matchesChart) {
        return;
      }
    }

    const xValue = xyValue[0];

    const newVerticalLines = [
      ...verticalLines.map((d) => ({ ...d, selected: false })),
      {
        xValue,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newVerticalLines, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, verticalLines, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempLine = isDefined(current) ? (
      <EachVerticalLine
        interactive={false}
        xValue={current.xValue}
        stroke={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.color || "#000000"
            : appearance.stroke || "#000000"
        }
        strokeWidth={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.width || 1
            : appearance.strokeWidth || 1
        }
        strokeOpacity={
          appearance.stroke && typeof appearance.stroke === "object"
            ? appearance.stroke.opacity !== undefined
              ? appearance.stroke.opacity
              : 1
            : appearance.strokeOpacity !== undefined
              ? appearance.strokeOpacity
              : 1
        }
        strokeDasharray={
          appearance.stroke &&
          typeof appearance.stroke === "object" &&
          appearance.stroke.dash
            ? appearance.stroke.dash
            : "Solid"
        }
        displayFormat={this.props.displayFormat}
        coordinate={appearance.coordinate}
        showCoordinate={false}
      />
    ) : null;

    return (
      <g>
        {verticalLines.map((each, idx) => {
          let eachAppearance = appearance;
          if (isDefined(each.appearance)) {
            eachAppearance = { ...appearance };
            if (
              each.appearance.stroke &&
              typeof each.appearance.stroke === "object"
            ) {
              eachAppearance.stroke = {
                ...(appearance.stroke || {}),
                ...(each.appearance.stroke || {}),
              };
            } else if (each.appearance.stroke) {
              eachAppearance.stroke = each.appearance.stroke;
            }
          }

          const hoverTextWithDefault = {
            ...VerticalLine.defaultProps.hoverText,
            ...hoverText,
          };

          let coordinateAppearance =
            eachAppearance.coordinate || appearance.coordinate || {};
          if (
            isDefined(each.appearance?.coordinate) &&
            typeof each.appearance.coordinate === "object"
          ) {
            coordinateAppearance = {
              ...(appearance.coordinate || {}),
              ...each.appearance.coordinate,
            };
          }

          const {
            displayFormat: coordinateDisplayFormat,
            show: coordinateShow,
            ...restCoordinateAppearance
          } = coordinateAppearance || {};

          const showCoordinate = coordinateShow !== false;
          const displayFormatFn =
            coordinateDisplayFormat || this.props.displayFormat;

          return (
            <EachVerticalLine
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              selected={each.selected}
              xValue={getValueFromOverride(
                override,
                idx,
                "xValue",
                each.xValue,
              )}
              stroke={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.color || "#000000"
                  : eachAppearance.stroke || "#000000"
              }
              strokeWidth={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.width || 1
                  : eachAppearance.strokeWidth || 1
              }
              strokeOpacity={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object"
                  ? eachAppearance.stroke.opacity !== undefined
                    ? eachAppearance.stroke.opacity
                    : 1
                  : eachAppearance.strokeOpacity !== undefined
                    ? eachAppearance.strokeOpacity
                    : 1
              }
              strokeDasharray={
                eachAppearance.stroke &&
                typeof eachAppearance.stroke === "object" &&
                eachAppearance.stroke.dash
                  ? eachAppearance.stroke.dash
                  : "Solid"
              }
              onDrag={this.handleDragVerticalLine}
              onDragComplete={this.handleDragVerticalLineComplete}
              hoverText={hoverTextWithDefault}
              displayFormat={displayFormatFn}
              coordinate={restCoordinateAppearance}
              showCoordinate={showCoordinate}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawVerticalLine}
          onClick={this.handleEnd}
          data-evo-id="interactive-vertical-line"
        />
      </g>
    );
  }
}

VerticalLine.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  snap: PropTypes.bool,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,
  displayFormat: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  hoverText: PropTypes.object,

  verticalLines: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
        dash: PropTypes.string,
      }),
    ]),
    coordinate: PropTypes.shape({
      fill: PropTypes.string,
      opacity: PropTypes.number,
      textFill: PropTypes.string,
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      stroke: PropTypes.string,
      strokeOpacity: PropTypes.number,
      strokeWidth: PropTypes.number,
      lineOpacity: PropTypes.number,
      lineStroke: PropTypes.string,
      rectWidth: PropTypes.number,
      rectHeight: PropTypes.number,
      arrowWidth: PropTypes.number,
      dy: PropTypes.number,
      orient: PropTypes.oneOf(["top", "bottom"]),
      at: PropTypes.oneOf(["top", "bottom"]),
    }),
  }).isRequired,
  chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

VerticalLine.defaultProps = {
  onStart: noop,
  onComplete: noop,
  onSelect: noop,
  shouldDisableSnap: () => true,
  snap: false,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 4,
  displayFormat: (value) => {
    if (value instanceof Date) {
      const formatter = timeFormat("%d/%m/%Y %H:%M");
      return formatter(value);
    }
    return String(value);
  },

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },

  verticalLines: [],

  appearance: {
    stroke: {
      color: "#2196F3",
      width: 2,
      opacity: 1,
      dash: "Solid",
    },
    coordinate: {
      fill: "#2196F3",
      opacity: 1,
      textFill: "#FFFFFF",
      fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
      fontSize: 12,
      fontWeight: "normal",
      stroke: "#2196F3",
      strokeOpacity: 1,
      strokeWidth: 1,
      lineOpacity: 0,
      lineStroke: "#2196F3",
      rectWidth: 80,
      rectHeight: 20,
      arrowWidth: 0,
      dy: 0,
      orient: "bottom",
      at: "bottom",
    },
  },
};

export default VerticalLine;
